'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  Package,
  Thermometer,
  Route,
  Timer,
  Shield,
  MapPin,
  Truck,
  FileCheck,
  Zap,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  TrendingUp,
  Globe,
  Building2,
  Droplet,
  Users,
  Clock,
  Activity,
  Navigation,
  Sparkles,
  Target,
} from 'lucide-react';
import { PAGE_HERO_IMAGES } from '@/lib/marketing-images';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

export default function LogisticsPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* ── Hero Section ────────────────────────────────────────── */}
      <section className="relative overflow-hidden gradient-hero pt-32 pb-20 px-6 lg:px-8">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0f0e2e]" />
        
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="max-w-7xl mx-auto relative z-10"
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-neon-amber to-neon-cyan mb-6"
              style={{ boxShadow: '0 0 30px rgba(245, 158, 11, 0.3)' }}
            >
              <Package className="w-10 h-10 text-white" />
            </motion.div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="text-white">From Warehouse</span>
              <br />
              <span className="text-gradient-warm">to Doorstep</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-8 leading-relaxed">
              End-to-end supply chain visibility with real-time cargo tracking, 
              proof-of-delivery, and cold-chain monitoring. Transform your logistics 
              operations with complete transparency from origin to destination.
            </p>

            <div className="mx-auto mb-8 max-w-4xl">
              <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
                <Image
                  src={PAGE_HERO_IMAGES.logisticsIndustry.src}
                  alt={PAGE_HERO_IMAGES.logisticsIndustry.alt}
                  width={1600}
                  height={900}
                  className="h-auto w-full rounded-2xl object-cover"
                  priority
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                href="/contact"
                className="px-8 py-4 rounded-xl btn-neon text-white font-semibold text-lg flex items-center gap-2 group"
              >
                Start Tracking Today
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href="/features"
                className="px-8 py-4 rounded-xl btn-neon-outline text-neon-amber font-semibold text-lg flex items-center gap-2"
              >
                Explore Features
                <Package className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>

          {/* Hero Visual */}
          <motion.div
            variants={itemVariants}
            className="mt-16 relative"
          >
            <div className="glass-card-heavy rounded-3xl p-8 md:p-12 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-neon-amber/10 via-transparent to-neon-cyan/10" />
              <div className="relative z-10 grid md:grid-cols-3 gap-6">
                {[
                  { icon: Package, label: 'Real-Time Tracking', color: 'text-neon-amber' },
                  { icon: Thermometer, label: 'Cold Chain Monitoring', color: 'text-neon-cyan' },
                  { icon: FileCheck, label: 'Digital POD', color: 'text-neon-purple' },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + idx * 0.1 }}
                    className="text-center"
                  >
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl glass-card mb-4 ${item.color}`}>
                      <item.icon className="w-8 h-8" />
                    </div>
                    <p className="text-white font-medium">{item.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ── Challenges Section ──────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
              <AlertTriangle className="w-5 h-5 text-neon-magenta" />
              <span className="text-sm font-semibold text-neon-magenta">The Challenges</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Logistics Operations</span>
              <br />
              <span className="text-gradient-warm">Under Pressure</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Modern supply chains face unprecedented challenges. 
              Visibility gaps lead to failures, losses, and customer dissatisfaction.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: AlertTriangle,
                title: 'Last-Mile Delivery Failures',
                desc: 'Failed deliveries cost time, money, and customer trust. No visibility means no accountability.',
                color: 'text-neon-magenta',
                bg: 'from-neon-magenta/10 to-transparent',
              },
              {
                icon: Thermometer,
                title: 'Cold Chain Breaks',
                desc: 'Temperature excursions destroy perishable goods. Without monitoring, losses go undetected until too late.',
                color: 'text-neon-cyan',
                bg: 'from-neon-cyan/10 to-transparent',
              },
              {
                icon: FileCheck,
                title: 'No Proof-of-Delivery',
                desc: 'Paper trails get lost. Disputes arise. Customers claim non-delivery. You have no digital evidence.',
                color: 'text-neon-purple',
                bg: 'from-neon-purple/10 to-transparent',
              },
              {
                icon: Shield,
                title: 'Theft in Transit',
                desc: 'Cargo disappears. High-value shipments vanish. No real-time alerts mean no chance to intervene.',
                color: 'text-neon-amber',
                bg: 'from-neon-amber/10 to-transparent',
              },
              {
                icon: Route,
                title: 'Route Inefficiency',
                desc: 'Drivers take longer routes. Fuel costs soar. Delivery windows missed. No optimization means wasted resources.',
                color: 'text-neon-teal',
                bg: 'from-neon-teal/10 to-transparent',
              },
              {
                icon: Clock,
                title: 'No ETA Visibility',
                desc: 'Customers call asking "where is my order?" You have no real-time updates. Communication breaks down.',
                color: 'text-neon-blue',
                bg: 'from-neon-blue/10 to-transparent',
              },
            ].map((challenge, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card rounded-2xl p-6 relative overflow-hidden group hover:scale-[1.02] transition-transform"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${challenge.bg} opacity-0 group-hover:opacity-100 transition-opacity`} />
                <div className="relative z-10">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl glass-card mb-4 ${challenge.color}`}>
                    <challenge.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{challenge.title}</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{challenge.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Solution Section ──────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-section-purple">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
              <Sparkles className="w-5 h-5 text-neon-amber" />
              <span className="text-sm font-semibold text-neon-amber">The Solution</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Complete Supply Chain</span>
              <br />
              <span className="text-gradient-cool">Visibility & Control</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Real-time tracking, temperature monitoring, digital proof-of-delivery, 
              and intelligent route optimization. Everything you need in one platform.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {[
              {
                icon: Package,
                title: 'Real-Time Cargo Tracking',
                desc: 'Track every shipment from warehouse to doorstep. GPS-enabled devices provide live location updates, route history, and delivery status. Customers see exactly where their order is.',
                features: ['Live GPS tracking', 'Route visualization', 'Delivery status updates', 'Customer portal'],
                color: 'text-neon-amber',
              },
              {
                icon: Thermometer,
                title: 'Temperature Monitoring',
                desc: 'Continuous cold-chain monitoring with temperature sensors. Instant alerts when thresholds are breached. Protect perishable goods and maintain compliance.',
                features: ['Real-time sensors', 'Temperature alerts', 'Compliance reports', 'Historical data'],
                color: 'text-neon-cyan',
              },
              {
                icon: FileCheck,
                title: 'Digital Proof-of-Delivery',
                desc: 'Eliminate paper trails. Digital signatures, photos, and timestamps captured at delivery. Instant access to delivery records. Resolve disputes quickly.',
                features: ['Digital signatures', 'Photo capture', 'Timestamp records', 'Instant access'],
                color: 'text-neon-purple',
              },
              {
                icon: Route,
                title: 'Route Optimization',
                desc: 'AI-powered route planning reduces fuel costs and delivery times. Dynamic rerouting based on traffic, weather, and priority. Maximize efficiency.',
                features: ['AI route planning', 'Dynamic rerouting', 'Fuel optimization', 'Multi-stop routes'],
                color: 'text-neon-teal',
              },
              {
                icon: Timer,
                title: 'ETA Prediction',
                desc: 'Accurate delivery time predictions powered by machine learning. Customers receive proactive updates. Reduce "where is my order?" calls.',
                features: ['ML-powered ETAs', 'Proactive updates', 'Customer notifications', 'Traffic integration'],
                color: 'text-neon-blue',
              },
              {
                icon: Shield,
                title: 'Theft Prevention',
                desc: 'Geofence alerts, tamper detection, and real-time monitoring prevent cargo theft. Instant notifications when shipments deviate from planned routes.',
                features: ['Geofence alerts', 'Tamper detection', 'Route deviation alerts', 'Security monitoring'],
                color: 'text-neon-amber',
              },
            ].map((solution, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-heavy rounded-2xl p-8 relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-neon-amber/5 via-transparent to-neon-cyan/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative z-10">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl glass-card mb-6 ${solution.color}`}>
                    <solution.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{solution.title}</h3>
                  <p className="text-slate-300 mb-6 leading-relaxed">{solution.desc}</p>
                  <ul className="space-y-2">
                    {solution.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-center gap-2 text-slate-300">
                        <CheckCircle2 className={`w-4 h-4 ${solution.color}`} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features Grid ──────────────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Comprehensive Logistics</span>
              <br />
              <span className="text-gradient-cool">Features</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Everything you need to optimize your supply chain operations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Thermometer, title: 'Cold Chain Sensors', desc: 'Real-time temperature and humidity monitoring' },
              { icon: FileCheck, title: 'Digital POD', desc: 'Electronic signatures and delivery confirmation' },
              { icon: Route, title: 'Route Optimization', desc: 'AI-powered route planning and optimization' },
              { icon: Timer, title: 'ETA Prediction', desc: 'Accurate delivery time estimates' },
              { icon: Shield, title: 'Cargo Security', desc: 'Theft prevention and tamper detection' },
              { icon: MapPin, title: 'Warehouse Geofencing', desc: 'Automated check-in/check-out at facilities' },
              { icon: Users, title: 'Driver Management', desc: 'Driver performance tracking and analytics' },
              { icon: CheckCircle2, title: 'Compliance', desc: 'Regulatory compliance and audit trails' },
              { icon: BarChart3, title: 'Analytics', desc: 'Comprehensive reporting and insights' },
              { icon: Zap, title: 'API Integrations', desc: 'Seamless integration with your systems' },
              { icon: Navigation, title: 'Live Tracking', desc: 'Real-time location updates and mapping' },
              { icon: Activity, title: 'Performance Metrics', desc: 'KPIs and operational dashboards' },
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="glass-card rounded-xl p-6 hover:scale-[1.02] transition-transform group"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg glass-card mb-4 text-neon-amber group-hover:text-neon-cyan transition-colors">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                <p className="text-slate-300 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use Cases Section ─────────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-section-purple">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Built for Every</span>
              <br />
              <span className="text-gradient-cool">Logistics Scenario</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              From FMCG distribution to pharmaceutical logistics
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Package,
                title: 'FMCG Distribution',
                desc: 'Fast-moving consumer goods tracking with real-time visibility and efficient last-mile delivery.',
                color: 'text-neon-amber',
              },
              {
                icon: Droplet,
                title: 'Pharmaceutical Logistics',
                desc: 'Temperature-controlled transport with strict compliance monitoring and chain of custody tracking.',
                color: 'text-neon-cyan',
              },
              {
                icon: Thermometer,
                title: 'Food & Beverage Cold Chain',
                desc: 'Perishable goods monitoring with temperature alerts and quality assurance throughout transit.',
                color: 'text-neon-purple',
              },
              {
                icon: Globe,
                title: 'E-Commerce Last-Mile',
                desc: 'Real-time package tracking, delivery notifications, and proof-of-delivery for online orders.',
                color: 'text-neon-teal',
              },
              {
                icon: Truck,
                title: 'Bulk Freight',
                desc: 'Heavy cargo tracking, route optimization, and delivery scheduling for industrial shipments.',
                color: 'text-neon-blue',
              },
              {
                icon: Building2,
                title: 'Cross-Border Transport',
                desc: 'International shipping visibility with customs tracking and multi-modal transport management.',
                color: 'text-neon-amber',
              },
            ].map((useCase, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-heavy rounded-2xl p-8 text-center group hover:scale-[1.02] transition-transform"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl glass-card mb-6 mx-auto ${useCase.color}`}>
                  <useCase.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{useCase.title}</h3>
                <p className="text-slate-300 leading-relaxed">{useCase.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats Section ───────────────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
              <TrendingUp className="w-5 h-5 text-neon-amber" />
              <span className="text-sm font-semibold text-neon-amber">Proven Results</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Transform Your</span>
              <br />
              <span className="text-gradient-cool">Operations</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Real results from real logistics operations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                stat: '45%',
                label: 'Fewer Delivery Failures',
                desc: 'Reduced failed deliveries through real-time tracking and proactive communication',
                icon: Target,
                color: 'text-neon-amber',
              },
              {
                stat: '30%',
                label: 'Fuel Reduction',
                desc: 'Lower fuel costs via intelligent route optimization and driver behavior insights',
                icon: Route,
                color: 'text-neon-cyan',
              },
              {
                stat: '99.8%',
                label: 'Cold Chain Compliance',
                desc: 'Maintained temperature integrity with continuous monitoring and instant alerts',
                icon: Thermometer,
                color: 'text-neon-purple',
              },
              {
                stat: 'Real-Time',
                label: 'ETA Accuracy',
                desc: 'Accurate delivery predictions keep customers informed and reduce support calls',
                icon: Timer,
                color: 'text-neon-teal',
              },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-heavy rounded-2xl p-8 text-center"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl glass-card mb-6 mx-auto ${stat.color}`}>
                  <stat.icon className="w-8 h-8" />
                </div>
                <div className={`text-5xl font-bold mb-4 ${stat.color}`}>
                  {stat.stat}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{stat.label}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{stat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ────────────────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-neon-amber to-neon-cyan mb-8 mx-auto"
              style={{ boxShadow: '0 0 30px rgba(245, 158, 11, 0.3)' }}
            >
              <Package className="w-10 h-10 text-white" />
            </motion.div>
            
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-white">Ready to Transform</span>
              <br />
              <span className="text-gradient-warm">Your Logistics?</span>
            </h2>
            
            <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto leading-relaxed">
              Join leading logistics companies using GlonexTrack to optimize operations, 
              reduce costs, and deliver exceptional customer experiences. 
              Start tracking today.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                href="/contact"
                className="px-10 py-5 rounded-xl btn-neon text-white font-semibold text-lg flex items-center gap-2 group"
              >
                Get Started Today
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href="/pricing"
                className="px-10 py-5 rounded-xl btn-neon-outline text-neon-amber font-semibold text-lg flex items-center gap-2"
              >
                View Pricing
                <TrendingUp className="w-5 h-5" />
              </Link>
            </div>
            
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-slate-300">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-neon-amber" />
                <span>No setup fees</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-neon-amber" />
                <span>30-day money-back guarantee</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-neon-amber" />
                <span>24/7 support</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
